import { FC, useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'react-feather';
import ReactGA from 'react-ga4';
import { useLocation, useNavigate } from 'react-router-dom';
import { InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import { AnimatedLogo } from '@/assets/animations/components/AnimatedLogo';

import { ErrorMessage } from '@/utils/Messages';

import { getUserInformation, login } from '@/services/User/request';
import { UserAuth } from '@/services/User/types';

import { useIdpAuth } from '@/context/IdpAuth/IdpAuthContext';
import { useToast } from '@/context/ToastContext';
import { useUser } from '@/context/UserContext';

import TextField from '@/components/TextField';

import packageJson from '../../../package.json';
import { PARTNER_DEFAULT_PATH } from '../Partners/pages/SsoEntry/handshake';
import { partnersRoutes } from '../Partners/types/paths';

import IdpLogin from './IdpLogin';
import LoginWithReCaptcha from './LoginWithReCaptcha';
import {
  AvisoDaMoldura,
  ButtonsContainer,
  ColoredBar,
  ForgetPasswordText,
  InputContainer,
  LinkDeAbaNova,
  LoginContainer,
  LogoImage,
  PageContainer,
  ProjectVersion,
  Text,
} from './styles';

import { resolverDestinoPosLogin, urlDoDestino } from '@/lib/auth/destinoPosLogin';
import { vaiParaFranquias } from '@/lib/auth/franquias';
import { captureLoginMethod } from '@/lib/idp/analytics';
import { dentroDeMoldura } from '@/lib/moldura';
import { captureError } from '@/lib/sentry';

export type ILoading = {
  is: boolean;
  status: 'Idle' | 'Success' | 'Error';
};

const Login: FC<{}> = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const walletUrl = import.meta.env.REACT_APP_PUBLIC_WALLET_URL;
  // Deep-link: o `RequireAuth` preserva em `state.from` a rota pedida antes do
  // login. Vale para os dois caminhos — a senha e o "Entrar com Seazone".
  const from = (
    location.state as {
      from?: { pathname: string; search?: string; hash?: string };
    } | null
  )?.from;
  const deepLink = from?.pathname
    ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
    : undefined;

  const { setUser, setUserInformation, clearSession } = useUser();
  // `idp-only` tira a senha da tela; `both` e `off` ficam como eram.
  const { passwordLogin } = useIdpAuth().config;
  // Dentro da moldura do portal o IDP nao abre (a tela dele recusa iframe):
  // sem a senha, a unica saida e o Sapron em aba nova.
  const semLoginNaMoldura = !passwordLogin && dentroDeMoldura();
  // A aba nova leva a rota que a moldura pedia (o `state.from` do
  // `RequireAuth`), como o pos-login da senha e do IDP levam. So rota do
  // Programa de Indicacao: e a unica que a moldura carrega, e o `/login`
  // nao e lugar de redirect aberto.
  const destinoDaAbaNova = deepLink?.startsWith(`/${partnersRoutes.partners.main}/`)
    ? deepLink
    : PARTNER_DEFAULT_PATH;

  const [type, setType] = useState<string>('password');
  const [loading, setLoading] = useState<ILoading>({
    is: false,
    status: 'Idle',
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },

    validationSchema: Yup.object().shape({
      email: Yup.string().required('E-mail obrigatório').email('Digite um e-mail válido'),
      password: Yup.string().min(6, 'No mínimo 6 caracteres').required('Senha obrigatória'),
    }),

    onSubmit: async (formData: UserAuth) => {
      try {
        setLoading((old) => ({ ...old, is: true, status: 'Idle' }));

        const { data } = await login(formData);

        // Acompanha a virada da flag: quanto do login ja entra pelo IDP e
        // quanto ainda entra pela senha.
        captureLoginMethod('legacy');

        setUser({
          email: formData.email,
          ...data,
        });

        const userDetails = await getUserInformation();

        const start = moment();
        if (!localStorage.getItem('startUse')) {
          localStorage.setItem('startUse', start.toString());
        }

        ReactGA.event(
          `${userDetails?.main_role}-${userDetails?.user_id}-${
            userDetails?.nickname || userDetails?.first_name
          }`,
          {
            category: `${userDetails?.main_role}-${userDetails?.user_id}-${
              userDetails?.nickname || userDetails?.first_name
            }`,
            action: 'Fez login',
          },
        );

        ReactGA.event(
          `${userDetails?.main_role}-${userDetails?.user_id}-${
            userDetails?.nickname || userDetails?.first_name
          }`,
          {
            category: `${userDetails?.main_role}-${userDetails?.user_id}-${
              userDetails?.nickname || userDetails?.first_name
            }`,
            action: `Último acesso ${moment().format('DD/MM/YYYY HH:mm')}`,
          },
        );

        localStorage.setItem(
          'userDetail',
          `${userDetails?.main_role}-${userDetails?.user_id}-${
            userDetails?.nickname || userDetails?.first_name
          }`,
        );

        ReactGA.set({ userId: userDetails?.user_id });

        if (userDetails) {
          await setUserInformation(userDetails);
          const destino = resolverDestinoPosLogin({
            usuario: userDetails,
            deepLink,
            franquias: await vaiParaFranquias(userDetails, formData.email),
          });
          const externa = urlDoDestino(destino, {
            walletUrl,
            franquiasUrl: import.meta.env.REACT_APP_PUBLIC_FRANQUIAS_URL,
            // Login por senha e sessao legada por definicao: o repasse por
            // token continua sendo o unico jeito de a Wallet abrir logada.
            comSessaoDoIdp: false,
            accessToken: data?.access,
          });
          if (externa !== undefined) {
            // Destino vem da env e o token da API; formData nao entra.
            // nosemgrep: js-open-redirect-from-function
            window.location.href = externa;
            return;
          }
          if (destino.tipo === 'rota') navigate(destino.rota);
          setLoading((old) => ({ ...old, is: false, status: 'Success' }));
        }
      } catch (e: unknown) {
        let shouldLogToSentry = true;
        const error = e as any;

        const isAuthError =
          error?.detail?.non_field_errors ||
          error?.status === 401 ||
          error?.response?.status === 401;

        // O `410` e o backend em `idp-only` recusando a senha: resposta esperada
        // de um bundle ainda em `both`, nao uma falha. A mensagem dele ja vem
        // em `error.message` e cai no toast pelo caminho comum.
        if (error?.detail?.non_field_errors || error?.statusCode === 410) {
          shouldLogToSentry = false;
        }

        if (shouldLogToSentry) {
          captureError({
            error: e,
            module: 'login',
            payload: {
              email: formik.values.email,
              originalErrorDetail: error?.detail,
            },
          });
        }

        if (isAuthError) {
          toast.error(ErrorMessage.invalidCredentials());
        } else if (e instanceof Error) {
          toast.error(e.message);
        } else {
          toast.error(ErrorMessage.default());
        }

        setLoading((old) => ({ ...old, is: false, status: 'Error' }));
      }
    },
  });

  useEffect(() => {
    // `clearSession`, e nao `logout`: abrir a tela de login derruba a sessao
    // local, mas nao pode mandar o navegador para o `end_session_endpoint` do
    // IDP — quem chega aqui quer entrar, nao sair.
    clearSession();
  }, []);

  useEffect(() => {
    document.title = 'Sapron | Login';
  }, []);

  return (
    <PageContainer>
      <ColoredBar>
        <LogoImage>
          <AnimatedLogo />
        </LogoImage>
      </ColoredBar>
      <form onSubmit={formik.handleSubmit}>
        <LoginContainer>
          <Text>Acesse o sistema</Text>
          <InputContainer>
            {passwordLogin && (
              <>
                <TextField
                  id="email"
                  dataCy="email"
                  type="email"
                  formik={formik}
                  placeholder="Seu e-mail"
                  startAdornment={
                    <InputAdornment position="start">
                      <Mail strokeWidth={'1'} size={20} />
                    </InputAdornment>
                  }
                />
                <TextField
                  id="password"
                  dataCy="password"
                  type={type}
                  placeholder="Senha"
                  formik={formik}
                  startAdornment={
                    <InputAdornment position="start">
                      <Lock strokeWidth={'1'} size={20} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="start">
                      {type === 'password' ? (
                        <EyeOff
                          onClick={() => setType('text')}
                          strokeWidth={'1'}
                          cursor="pointer"
                          size={20}
                        />
                      ) : (
                        <Eye
                          onClick={() => setType('password')}
                          strokeWidth={'1'}
                          cursor="pointer"
                          size={20}
                        />
                      )}
                    </InputAdornment>
                  }
                />
              </>
            )}
            <ButtonsContainer>
              {passwordLogin && (
                <>
                  <ForgetPasswordText to="/recuperar-senha">Esqueci a senha</ForgetPasswordText>

                  <LoginWithReCaptcha loading={loading} />
                </>
              )}
              {semLoginNaMoldura ? (
                <>
                  <AvisoDaMoldura>
                    Não foi possível entrar automaticamente no Programa de Indicação.
                  </AvisoDaMoldura>
                  <LinkDeAbaNova href={destinoDaAbaNova} target="_blank" rel="noopener">
                    Abrir o Sapron em nova aba
                  </LinkDeAbaNova>
                </>
              ) : (
                <IdpLogin returnTo={deepLink} />
              )}
            </ButtonsContainer>
          </InputContainer>
          <ProjectVersion>
            <p>
              Versão <strong>{packageJson.version}</strong>
            </p>
          </ProjectVersion>
        </LoginContainer>
      </form>
    </PageContainer>
  );
};

export default Login;
